import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CATEGORIES, EVENTS, FITS, label } from "./bits";

// Section 7: every judgment has a "wrong" button. The correction is stored and the 30 most recent become worked examples.
export function CorrectDialog({ judgment, open, onClose, onDone }) {
  const [f, setF] = useState({ category: "", event: "", fit: "", pursuit_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  if (!judgment) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.reason.trim()) { toast.error("Say why — the judge learns from it"); return; }
    setBusy(true);
    try {
      const fields = {};
      ["category", "event", "fit"].forEach((k) => { if (f[k]) fields[k] = f[k]; });
      if (f.pursuit_id.trim()) fields.pursuit_id = f.pursuit_id.trim();
      if (f.pursuit_id.trim().toLowerCase() === "none") { delete fields.pursuit_id; fields.pursuit_type = "none"; }
      await api.post(`/bd/judgments/${judgment.judgment_id}/correct`, { fields, reason: f.reason.trim() });
      toast.success("Correction saved — the judge will use it as a worked example");
      setF({ category: "", event: "", fit: "", pursuit_id: "", reason: "" });
      onDone && onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the correction"); }
    setBusy(false);
  };
  const Sel = ({ k, opts, current }) => (
    <select value={f[k]} onChange={set(k)} className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`bd-correct-${k}`}>
      <option value="">Keep “{label(current) || "—"}”</option>
      {opts.map((o) => <option key={o} value={o}>{label(o)}</option>)}
    </select>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bd-correct-dialog">
        <DialogHeader><DialogTitle className="text-base">This judgment is wrong</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 -mt-2 truncate">{judgment.from_email} · {judgment.subject}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Category</Label><Sel k="category" opts={CATEGORIES} current={judgment.category} /></div>
          <div><Label className="text-xs">Event</Label><Sel k="event" opts={EVENTS} current={judgment.event} /></div>
          <div><Label className="text-xs">Fit</Label><Sel k="fit" opts={FITS} current={judgment.fit} /></div>
        </div>
        <div>
          <Label className="text-xs">Pursuit (bid_… / lead_… / none)</Label>
          <Input value={f.pursuit_id} onChange={set("pursuit_id")} placeholder={judgment.pursuit?.id || "leave empty to keep"} data-testid="bd-correct-pursuit" />
        </div>
        <div>
          <Label className="text-xs">Why (required)</Label>
          <Textarea value={f.reason} onChange={set("reason")} rows={3} placeholder="e.g. This is our own outgoing proposal — we sent a price." data-testid="bd-correct-reason" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="bd-correct-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#003E87] hover:bg-[#002a5c] text-white" data-testid="bd-correct-save">Save correction</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
